/**
 * Task 128 D3 — useWorkblocksDay
 *
 * Returns a single day's work/break block timeline for an employee. Used by
 * `DayTimelineCard` to render the proportional timeline bar on the range
 * page.
 *
 * In mockup mode the hook returns `null` immediately so the range page
 * falls through to the existing seed-based `buildDayBlocksFromDayType`
 * path — preserves mockup snapshot stability.
 *
 * In live mode the hook fetches
 *   GET /api/v1/orgs/:orgId/employees/:employeeId/workblocks?date=YYYY-MM-DD
 * via react-query. The per-day cache key `(orgId, employeeId, date)`
 * makes subsequent renders of the same day on the same page cheap cache
 * hits.
 *
 * The backend response shape (see web/src/dashboard/work-blocks.ts and
 * web/src/routes/dashboard.ts:1322) differs slightly from the view-model
 * the page consumes — this hook adapts block fields (`type`→`kind`,
 * `startTime`→`startAt`, `endTime`→`endAt`) and projects the dominant
 * project into `projectId`/`projectName` at the top level. The page never
 * touches the raw shape.
 */
import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { WorkBlock, WorkBlocksDayResponse } from '@/types/dashboard';

interface UseWorkblocksDayResult {
  data: WorkBlocksDayResponse | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Raw on-wire shape produced by the backend route. Kept internal —
 * consumers see the adapted `WorkBlocksDayResponse` instead.
 */
interface RawWorkBlock {
  type: 'work' | 'break';
  startTime: string;
  endTime: string;
  durationMinutes: number;
  eventCount: number;
  projects: Array<{
    projectId: string | null;
    projectName: string | null;
    billable: boolean | null;
    eventCount: number;
    minutes: number;
  }>;
}

interface RawWorkblocksDayResponse {
  date: string;
  summary: {
    totalWorkMinutes: number;
    totalBreakMinutes: number;
    sessions: number;
    eventCount: number;
    firstEvent: string | null;
    lastEvent: string | null;
  };
  blocks: RawWorkBlock[];
}

function adaptBlock(raw: RawWorkBlock): WorkBlock {
  // The backend already sorts `projects` by `eventCount DESC` in
  // computeWorkBlocks; index 0 is the dominant project. For break blocks
  // the array is empty, so both fields collapse to null.
  const dominant = raw.projects[0] ?? null;
  return {
    kind: raw.type,
    startAt: raw.startTime,
    endAt: raw.endTime,
    durationMinutes: raw.durationMinutes,
    projectId: dominant?.projectId ?? null,
    projectName: dominant?.projectName ?? null,
    eventCount: raw.eventCount,
  };
}

function adapt(raw: RawWorkblocksDayResponse): WorkBlocksDayResponse {
  return {
    date: raw.date,
    blocks: raw.blocks.map(adaptBlock),
    summary: raw.summary,
  };
}

export function useWorkblocksDay(
  employeeId: string | null,
  date: string | null,
): UseWorkblocksDayResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery<WorkBlocksDayResponse>({
    queryKey: ['workblocksDay', orgId, employeeId, date],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (date) qs.set('date', date);
      const raw = await apiFetch<RawWorkblocksDayResponse>(
        `/api/v1/orgs/${orgId}/employees/${employeeId}/workblocks?${qs.toString()}`,
        token,
      );
      return adapt(raw);
    },
    enabled: !mockupMode && !!orgId && !!employeeId && !!date,
  });

  if (mockupMode) {
    // Mockup mode defers to the fixture seed-based layout in the page.
    return { data: null, isLoading: false, error: null };
  }

  if (!employeeId || !date) {
    return { data: null, isLoading: false, error: null };
  }

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}
